import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Star, MessageSquare, ThumbsUp, ThumbsDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AdminFeedback = ({ user }) => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, full_survey, quick_prompt

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback`, {
        headers: { 'admin-id': user.id }
      });
      setFeedback(response.data.feedback || []);
    } catch (error) {
      console.error('Error loading feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const filteredFeedback = filter === 'all' 
    ? feedback 
    : feedback.filter(f => f.type === filter);

  const avgRating = feedback.length > 0
    ? (feedback.filter(f => f.rating).reduce((sum, f) => sum + f.rating, 0) / feedback.filter(f => f.rating).length).toFixed(1)
    : 0;

  const fullSurveyCount = feedback.filter(f => f.type === 'full_survey').length;
  const quickPromptCount = feedback.filter(f => f.type === 'quick_prompt').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="text-white">Loading feedback...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      {/* Navigation */}
      <nav className="bg-[#1C1917] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-white/60" />
            </button>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-xl font-serif font-bold text-white">VowFlow</span>
            <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full ml-2">ADMIN</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">Test User Feedback</h1>
          <p className="text-white/50">Feedback from VFLOWTEST trial users</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <Star size={20} />
              <span className="text-3xl font-bold">{avgRating}</span>
            </div>
            <p className="text-sm opacity-90">Average Rating</p>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <MessageSquare size={20} />
              <span className="text-3xl font-bold">{feedback.length}</span>
            </div>
            <p className="text-sm opacity-90">Total Responses</p>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp size={20} />
              <span className="text-3xl font-bold">{fullSurveyCount}</span>
            </div>
            <p className="text-sm opacity-90">Full Surveys</p>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <MessageSquare size={20} />
              <span className="text-3xl font-bold">{quickPromptCount}</span>
            </div>
            <p className="text-sm opacity-90">Quick Responses</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-[#881337] text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            All ({feedback.length})
          </button>
          <button
            onClick={() => setFilter('full_survey')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'full_survey'
                ? 'bg-[#881337] text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            Full Surveys ({fullSurveyCount})
          </button>
          <button
            onClick={() => setFilter('quick_prompt')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'quick_prompt'
                ? 'bg-[#881337] text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            Quick Responses ({quickPromptCount})
          </button>
        </div>

        {/* Feedback List */}
        {filteredFeedback.length === 0 ? (
          <div className="bg-[#1C1917] rounded-2xl p-12 text-center border border-white/10">
            <MessageSquare className="mx-auto text-white/20 mb-4" size={48} />
            <p className="text-white/50">No feedback received yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFeedback.map((item) => (
              <div key={item.id} className="bg-[#1C1917] rounded-xl p-6 border border-white/10">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-white">{item.couple_names || 'Anonymous'}</h3>
                    <p className="text-sm text-white/40">{item.user_email || 'No email'}</p>
                    <p className="text-xs text-white/30 mt-1">
                      {new Date(item.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {item.type === 'full_survey' && (
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                        Full Survey
                      </span>
                    )}
                    {item.type === 'quick_prompt' && (
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs">
                        Day {item.day_number} Check-in
                      </span>
                    )}
                    {item.rating && (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className={i < item.rating ? 'fill-amber-500 text-amber-500' : 'text-white/20'}
                          />
                        ))}
                      </div>
                    )}
                    {item.thumbs && (
                      <div>
                        {item.thumbs === 'up' ? (
                          <ThumbsUp className="text-green-400" size={20} />
                        ) : (
                          <ThumbsDown className="text-red-400" size={20} />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Full Survey Fields */}
                {item.type === 'full_survey' && (
                  <div className="space-y-3 text-sm">
                    {item.ease_of_use && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/60 text-xs mb-1">Ease of Use:</p>
                        <p className="text-white">{item.ease_of_use}</p>
                      </div>
                    )}
                    {item.most_helpful_feature && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/60 text-xs mb-1">Most Helpful Feature:</p>
                        <p className="text-white">{item.most_helpful_feature}</p>
                      </div>
                    )}
                    {item.missing_features && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/60 text-xs mb-1">Missing Features:</p>
                        <p className="text-white">{item.missing_features}</p>
                      </div>
                    )}
                    {item.would_recommend && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/60 text-xs mb-1">Would Recommend:</p>
                        <p className="text-white">{item.would_recommend}</p>
                      </div>
                    )}
                    {item.would_purchase && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/60 text-xs mb-1">Would Purchase:</p>
                        <p className="text-white">{item.would_purchase}</p>
                      </div>
                    )}
                    {item.overall_feedback && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/60 text-xs mb-1">Overall Feedback:</p>
                        <p className="text-white">{item.overall_feedback}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Prompt Fields */}
                {item.type === 'quick_prompt' && (
                  <div className="space-y-2 text-sm">
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-white/60 text-xs mb-1">{item.prompt_title}: {item.prompt_question}</p>
                      {item.comment && <p className="text-white mt-2">{item.comment}</p>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedback;
